import { useState, useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { GenerationDetailDTO } from '@/lib/types'

export interface ImageItem {
  generationId: string
  assetId: string
  assetType: string
  src?: string | null
  downloadUrl?: string | null
  index?: number
  isVideo?: boolean
}

export function useImagePopup() {
  const [isOpen, setIsOpen] = useState(false)
  const [images, setImages] = useState<ImageItem[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [loadingGenerations, setLoadingGenerations] = useState<Set<string>>(new Set())

  // Query for generation details
  const generationQueries = useQuery({
    queryKey: ['image-popup', 'generations', Array.from(loadingGenerations)],
    queryFn: async () => {
      const promises = Array.from(loadingGenerations).map(async (generationId) => {
        try {
          const detail = await api.generation.detail(generationId, true)
          return { generationId, detail }
        } catch (error) {
          console.error(`Failed to load details for generation ${generationId}:`, error)
          return { generationId, detail: null }
        }
      })
      const results = await Promise.all(promises)
      return results.reduce((acc, { generationId, detail }) => {
        if (detail) acc[generationId] = detail
        return acc
      }, {} as Record<string, GenerationDetailDTO>)
    },
    enabled: loadingGenerations.size > 0,
  })

  // Update images when generation details are loaded
  useEffect(() => {
    if (generationQueries.data && images.length > 0) {
      const updatedImages = images.map(img => {
        const detail = generationQueries.data[img.generationId]
        if (detail) {
          // Find the corresponding full-resolution asset
          const fullAsset = detail.assets.find(asset =>
            (asset.asset_type.startsWith('image_full') || asset.asset_type.startsWith('video')) &&
            (asset.index ?? 0) === img.index
          )

          if (fullAsset) {
            return {
              ...img,
              src: fullAsset.asset_url ?? fullAsset.download_url,
              downloadUrl: fullAsset.download_url ?? fullAsset.asset_url,
              assetType: fullAsset.asset_type,
              isVideo: fullAsset.asset_type.startsWith('video'),
            }
          }
        }
        return img
      })
      setImages(updatedImages)
    }
  }, [generationQueries.data, images.length])

  const openPopup = useCallback((newImages: ImageItem[], startIndex: number = 0) => {
    if (newImages.length === 0) return

    setImages(newImages)
    setCurrentImageIndex(Math.min(startIndex, newImages.length - 1))

    // Load generation details for all images
    const generationIds = new Set(newImages.map(img => img.generationId))
    setLoadingGenerations(generationIds)

    setIsOpen(true)
  }, [])

  const closePopup = useCallback(() => {
    setIsOpen(false)
    setImages([])
    setCurrentImageIndex(0)
    setLoadingGenerations(new Set())
  }, [])

  const goToImage = useCallback((index: number) => {
    if (index >= 0 && index < images.length) {
      setCurrentImageIndex(index)
    }
  }, [images.length])

  const goToNextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length)
  }, [images.length])

  const goToPreviousImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
  }, [images.length])

  const regenerate = useCallback(async (generationId: string): Promise<void> => {
    await api.generation.regenerate(generationId)
    closePopup()
  }, [closePopup])

  const toggleFavorite = useCallback(async (assetId: string): Promise<void> => {
    await api.generation.toggleFavorite(assetId)
  }, [])

  return {
    isOpen,
    openPopup,
    closePopup,
    images,
    currentImageIndex,
    setCurrentImageIndex: goToImage,
    goToNextImage,
    goToPreviousImage,
    generationDetails: generationQueries.data || {},
    isLoadingDetails: generationQueries.isLoading,
    regenerate,
    toggleFavorite,
  }
}
